import React, {useCallback, useEffect} from 'react';
import {
  Form, Input, Modal, Skeleton,
} from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  selectEditState,
  selectEmailRecipientsRequest,
  selectCreateEmailRecipientRequest,
} from '../../../../state/ui/configuration/communication/emailRecipientsUiReducer';
import { selectEmailRecipient } from '../../../../state/domain/communication/emailRecipientReducer';
import { cancelEditEmailRecipient, requestAddEmailRecipient } from '../../../../state/ui/configuration/communication/emailRecipientsUiActions';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';



// FIELDS
/**
 * The field for the description label.  Allows clear, and max length set.  Required plus extra duplicate validation.
 * The rules are injected into the form using the form field decorator.
 */
const EmailAddressField = injectIntl(({
  form, emailRecipient, intl, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="email" />}>
    { form.getFieldDecorator('emailAddress', {
      initialValue: emailRecipient ? emailRecipient.emailAddress : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmail' }) },
        { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
        {
          validator(rule, value, callback) {
            if (value !== (emailRecipient ? emailRecipient.emailAddress : '')
              && checkForDuplicates(emailRecipient ? emailRecipient.emailAddress : null, value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={80}
        disabled={!!emailRecipient}
        allowClear={!emailRecipient}
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

// MODAL AND FORM
const EditEmailRecipient = injectIntl(Form.create({ name: 'edit-email-recipient' })(({
  intl, form, visible, setVisible, emailRecipient, onCreate, onUpdate, emailAddressValidator, sessionUser,
  onCancel, isLoading, isSubmitting, validationBox, manageCompany, ...rest
}) => {
  // Current and set of locations
  // const [selectValue, setSelectValue] = useState(null);
  const isCreating = !emailRecipient;
  const titleId = isCreating ? 'createEmailRecipient' : 'createEmailRecipient';
  const buttonId = isCreating ? 'save' : 'save';
  const { resetFields } = form;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();
    }
  }, [emailRecipient, resetFields, visible, sessionUser]);

  // On form submit.
  const submitForm = useCallback(() => {
    form.validateFields((err, values) => {
      if (!err) {

          // Save Object
          const newEmailRecipient = emailRecipient
              ? {
                ...emailRecipient,
                emailAddress: values.emailAddress,
              }
              : {
                emailAddress: values.emailAddress,
              };

          const submitFunction = isCreating ? onCreate : onUpdate;
          submitFunction(newEmailRecipient, manageCompany);
      }
    });
  }, [form, emailRecipient, isCreating, onCreate, onUpdate, manageCompany]);

  return (
    <Modal
      title={<FormattedMessage id={titleId} />}
      okText={<FormattedMessage id={buttonId} />}
      onOk={submitForm}
      visible={visible}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      style={{ top: '2px' }}
      {...rest}
    >
      {isSubmitting
          ?
          <div>
            <Skeleton title={false} paragraph={{ width: [50, 200, 200, 100, 100, 50, 200, 50, 100, 100, 100, 100, 100, 50, 200, 200, 200, 200, 200, 200, 70] }} />
          </div>
          :
          <Form layout="vertical" onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}>
            <ViaValidationAlert validationError={validationBox}/>
            <EmailAddressField form={form} emailRecipient={emailRecipient} checkForDuplicates={emailAddressValidator}/>
          </Form>
      }
    </Modal>
  );
}));

// Validate the prop type parameters.
EditEmailRecipient.propTypes = {
  visible: PropTypes.bool.isRequired,
  emailRecipient: PropTypes.shape({
    emailAddress: PropTypes.string.isRequired,
  }),
  sessionUser: PropTypes.shape({
    emailAddress: PropTypes.string.isRequired,
  }),
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
};

EditEmailRecipient.defaultProps = {
  emailRecipient: null,
  isLoading: false,
  validationBox: null,
};

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectCreateEmailRecipientRequest(state);
  const pageDataRequest = selectEmailRecipientsRequest(state);

  const selectedEmailRecipient = editState.recipientId ? selectEmailRecipient(state, editState.recipientId) : null;

  return {
    visible: editState.isEditing,
    emailRecipient: selectedEmailRecipient,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
    sessionUser: selectSessionUser(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: (recipient, manageCompany) => dispatch(requestAddEmailRecipient(recipient, manageCompany)),
  onCancel: () => dispatch(cancelEditEmailRecipient()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditEmailRecipient);
